import React, { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import SingleAdminForm from "./SingleAdminForm";
import SingleAdminTable from "./SingleAdminTable";
import AdminUserAPI from "../../api/AdminUserAPI";
import { DEFAULT_USER_ROLE_BY_SCOPE } from "../../constants/roles";
import AdminUser from "../../models/AdminUser";
import AdminUserRoles from "../../models/AdminUserRoles";
import PageHeader from "../common/PageHeader";
import { ADMIN_USERS_ROUTE } from "../routes/routes";
import Spinner from "../utils/Spinner";

import type { JSX } from "react";

import type { AdminUserToCreate } from "../../api/AdminUserAPI.types";

const infoPanelLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000097713-editor-management-create-different-accounts-for-your-back-office-editors",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000097713-gestion-des-%C3%A9diteurs-cr%C3%A9ez-diff%C3%A9rents-comptes-pour-les-%C3%A9diteurs-de-votre-back-office",
};

function SingleAdminPage(): JSX.Element {
  const { t, i18n } = useTranslation("editor-users");
  const { id } = useParams<{ id: string }>();
  const isNewUser = id === "new";

  const getSingleAdminUserQuery = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getSingleAdminUser,
    params: [id],
  });

  const {
    data: adminUserData,
    isError: fetchAdminUserError,
    isLoading: isLoadingAdminUser,
  } = useQuery({
    ...getSingleAdminUserQuery,
    enabled: !isNewUser,
  });

  const getAdminUserRolesQuery = AdminUserAPI.generateQuery({
    fn: AdminUserAPI.getAdminUserRoles,
    params: [id],
  });

  const {
    isLoading: isLoadingAdminUserRoles,
    data: adminUserRolesData,
    isError: isErrorAdminUserRoles,
    refetch: refetchAdminUserRoles,
  } = useQuery({
    ...getAdminUserRolesQuery,
    enabled: !isNewUser,
  });

  const history = useHistory();

  const goToAdminUsersPage = (): void => history.goBack();

  const [user, setUser] = useState<AdminUserToCreate | AdminUser>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    avatarUrl: "",
    uuid: "",
    company: "",
    status: "",
    adminUserRoles: new AdminUserRoles(
      DEFAULT_USER_ROLE_BY_SCOPE,
      i18n.language,
    ),
  });

  useEffect(() => {
    if (
      !isLoadingAdminUser &&
      !isLoadingAdminUserRoles &&
      !fetchAdminUserError &&
      adminUserData &&
      adminUserRolesData
    ) {
      const updatedAdminUser = new AdminUser({
        avatarUrl: adminUserData.avatar_url,
        firstName: adminUserData.first_name,
        lastName: adminUserData.last_name,
        email: adminUserData.email,
        uuid: adminUserData.uuid,
        company: adminUserData.company ?? null,
        status: adminUserData.status,
        userRoles: adminUserRolesData,
      });
      setUser(updatedAdminUser);
    }
  }, [
    isLoadingAdminUser,
    fetchAdminUserError,
    id,
    adminUserData,
    isLoadingAdminUserRoles,
    adminUserRolesData,
    i18n.language,
  ]);

  const breadcrumbs = [
    {
      name: t("manageUsers"),
      url: ADMIN_USERS_ROUTE,
    },
    {
      name: user?.uuid ? t("editEditor") : t("addEditor"),
    },
  ];

  return (
    <div className="editor-container">
      <div className="editor-project-form-container editor-has-breadcrumbs">
        {!isNewUser && isLoadingAdminUser ? (
          <div className="admin-user-form_spinner-container">
            <Spinner />
          </div>
        ) : (
          <React.Fragment>
            <PageHeader
              breadcrumbs={breadcrumbs}
              infoPanelLinks={infoPanelLinks}
              onClickBack={goToAdminUsersPage}
              title={
                user?.uuid ? `${user.firstName} ${user.lastName}` : t("userNew")
              }
            />
            {fetchAdminUserError ? <div>{t("error:genericError")}</div> : null}
            {user ? (
              <React.Fragment>
                <SingleAdminForm user={user} />
                {!isNewUser && (
                  <SingleAdminTable
                    isError={isErrorAdminUserRoles}
                    isLoading={isLoadingAdminUserRoles}
                    onRetry={refetchAdminUserRoles}
                    roles={user.adminUserRoles}
                    userId={user.uuid}
                  />
                )}
              </React.Fragment>
            ) : null}
          </React.Fragment>
        )}
      </div>
    </div>
  );
}

export default SingleAdminPage;
